import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IDataSourceApiService, IEntity, IEntityMetadata, IMetaFunctionArgs, INamespace } from 'interfaces-lib';
import { jwtDecode } from 'jwt-decode';
import { forkJoin, Observable } from 'rxjs';
import { concatMap, map, mergeAll, tap, toArray } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';

export const BullhornNamespace: INamespace = { name: 'bullhorn', aliases: [] };

export enum BullhornListTypes {
  FileAttachments = 'fileAttachments',
}

export enum BullhornFileTypes {
  FileAttachment = 'fileAttachment',
}

export enum BullhornEntityTypes {
  Candidate = 'Candidate',
  ClientCorporation = 'ClientCorporation',
  CorporateUser = 'CorporateUser',
  JobOrder = 'JobOrder',
  JobSubmission = 'JobSubmission',
  Placement = 'Placement',
  ClientContact = 'ClientContact',
  Opportunity = 'Opportunity',
  Lead = 'Lead',
  Note = 'Note',
  Appointment = 'Appointment',
  Task = 'Task',
  Company = 'Company',
  CertificationFileAttachment = 'CertificationFileAttachment',
  Certification = 'Certification',
  CertificationGroup = 'CertificationGroup',
  CandidateCertificationRequirement = 'CandidateCertificationRequirement',
  JobSubmissionCertificationRequirement = 'JobSubmissionCertificationRequirement',
  PlacementCertification = 'PlacementCertification',
}

const bullhornEntityTypeMap: Record<string, BullhornEntityTypes> = {
  'Job Posting': BullhornEntityTypes.JobOrder,
  'Client': BullhornEntityTypes.ClientCorporation,
  'Client Contact': BullhornEntityTypes.ClientContact,
};

export function normaliseBullhornEntityType(entityType: string): string {
  return bullhornEntityTypeMap[entityType] ?? entityType;
}

export interface IBody {
  [key: string]: any;
}

interface IResponse {
  total?: number; // /search only
  start?: number; // /search only
  count?: number; // /search only
  data: any[]; // get (/entity) and query (/search)
}

interface IEntitySearchMap {
  [index: string]: IEntitySearchDetails;
}

interface IEntitySearchDetails {
  fields: string[];
  includeIsDeleted: boolean;
  queryFields?: string[];
}

@Injectable({ providedIn: 'root' })
export class BullhornDataSourceApiService implements IDataSourceApiService {
  /*
    Used for building the search body
  */
  private entitySearchMap: IEntitySearchMap = {
    [BullhornEntityTypes.Candidate]: {
      fields: ['id', 'firstName', 'lastName', 'companyName', 'occupation'],
      includeIsDeleted: true,
    },
    [BullhornEntityTypes.ClientCorporation]: {
      fields: ['id', 'name'],
      includeIsDeleted: false,
    },
    [BullhornEntityTypes.JobOrder]: {
      fields: ['id', 'title', 'clientCorporation'],
      includeIsDeleted: true,
    },
    [BullhornEntityTypes.Placement]: {
      fields: ['id', 'candidate', 'jobOrder', 'clientCorporation'],
      includeIsDeleted: false,
    },
    [BullhornEntityTypes.ClientContact]: {
      fields: ['id', 'name', 'clientCorporation'],
      includeIsDeleted: true,
    },
    [BullhornEntityTypes.Opportunity]: {
      fields: ['id', 'title', 'clientCorporation'],
      includeIsDeleted: true,
    },
    [BullhornEntityTypes.Certification]: {
      fields: ['id', 'name'],
      includeIsDeleted: true,
    },
    [BullhornEntityTypes.CertificationGroup]: {
      fields: ['id', 'name'],
      includeIsDeleted: true,
    },
  };

  constructor(
    private http: HttpClient,
    private identityStorage: IdentityStorage,
  ) {
    console.debug('BullhornDataSourceApiService created');
  }

  get(entity: IEntity, getParams?: { fields: string }): Observable<any> {
    const fields = getParams?.fields ?? '';
    const url = this.buildApiUrl(`entity/${entity.type}/${entity.id}?fields=${fields}`);
    return this.http.get<IResponse>(url).pipe(
      // force to match the IEntity interface
      map((response) => ({
        ...response.data,
        id: entity.id.toString(),
        type: entity.type,
      })),
    );
  }

  list(listParams: any): Observable<any[]> {
    return this.retrieveRecords({ searchType: 'search', ...listParams });
  }

  // TODO: this is the start of making this service just be a wrapper around the API calls instead of knowing what all the callers want!
  list2(listParams: any): Observable<any[]> {
    console.debug('BullhornApi.list2', listParams);
    const url = this.buildApiUrl(`search/${listParams.entity.type}`);
    const bodyCounter: IBody = {
      query: listParams.query,
      fields: listParams.fields,
      count: 1,
      showTotalMatched: true,
    };

    const buildObs = (first: any) => {
      const totalToPull = first.total;
      const pageSize = listParams.pageSize ?? 500;
      const pageCount = Math.ceil(totalToPull / pageSize);
      const obs: any[] = [];
      for (let i = 1; i <= pageCount; i++) {
        const start = (i - 1) * pageSize;
        const parameters = `?query=${listParams.query}&fields=${listParams.fields}&start=${start}&count=${pageSize}`;
        obs.push(this.http.get<any>(url + parameters));
      }
      return obs;
    };

    const first$ = this.http.post<any>(url, bodyCounter);
    return first$.pipe(
      map((first) => buildObs(first)),
      concatMap((obs) =>
        forkJoin(...obs).pipe(
          concatMap((r) => r.map((rr) => (rr as { data: any }).data)),
          mergeAll(),
          toArray(),
        ),
      ),
      tap((res) => console.debug(`${res.length} pulled for ${listParams.entity.type}, query=${listParams.query}.`)),
    );
  }

  ping(): Observable<boolean> {
    const url = this.buildApiUrl('ping');
    return this.http.get(url).pipe(
      tap((pingResponse) => console.debug('pingResponse', pingResponse)),
      map((_) => true),
    );
  }

  // TODO: BW - consider passing the query fields and response fields
  query(entityType: string, queryParams: { searchTerm: string; count: number; queryFields: string[] }): Observable<IEntity[]> {
    const url = this.buildApiUrl(`search/${entityType}`);
    const body = this.constructSearchRequestBody(entityType, queryParams);
    return this.http.post<IResponse>(url, body).pipe(
      map((response) =>
        response.data.map((entity) => ({
          ...entity,
          id: entity.id.toString(),
          type: entityType,
        })),
      ),
    );
  }

  // TODO: this is the start of making this service just be a wrapper around the API calls instead of knowing what all the callers want!
  query2(entityType: string, queryParams: any): Observable<any[]> {
    const params = {
      searchType: 'query',
      entity: { type: entityType },
      ...queryParams,
    };
    return this.retrieveRecords(params);
  }

  meta(args: IMetaFunctionArgs): Observable<IEntityMetadata> {
    const url = this.buildApiUrl(`meta/${args.entity}?meta=basic&fields=${args.fields.join(',')}`);
    return this.http.get(url).pipe(
      map(
        (response: any) =>
          <IEntityMetadata>{
            name: response.entity,
            label: response.label,
            fields: response.fields,
          },
      ),
    );
  }

  settings(settings: string): Observable<any> {
    const url = this.buildApiUrl(`settings/${settings}`);
    return this.http.get<any>(url);
  }

  buildApiUrl(path: string): string {
    const restUrl = BullhornDataSourceApiService.getUrlEndpoint((this.identityStorage.get() as any).token);
    return `${restUrl}/${path}`;
  }

  private constructSearchRequestBody(entityType: string, queryParams: { searchTerm: string; count: number; queryFields: string[] }): IBody {
    const body: IBody = {
      count: queryParams.count ?? 20,
    };

    queryParams.searchTerm = queryParams.searchTerm.trim();

    const entityInfo: IEntitySearchDetails = {
      ...this.entitySearchMap[entityType],
      queryFields: queryParams.queryFields,
    };
    if (!entityInfo) {
      throw new Error(`Unknown entity type '${entityType}' building query.`);
    }

    body['query'] = this.generateSearchQuery(queryParams.searchTerm, entityInfo);
    body['fields'] = entityInfo.fields.join(',');

    return body;
  }

  private generateSearchQuery = (searchTerm: string, entityInfo: IEntitySearchDetails) => {
    const { queryFields, includeIsDeleted } = entityInfo;
    const isDigit = /^\d+$/.test(searchTerm);

    // Constructing the query based on search term
    let queryString = '';
    if (!isDigit && (queryFields as string[]).length > 0) {
      // Loop over query fields and add wildcard search
      queryString = `(${queryFields?.map((q) => this.generateSearchClause(q, searchTerm)).join(' OR ')})`;
    }

    // Adding conditions for digit search
    if (isDigit) {
      queryString = `(id:${searchTerm} OR phone:${searchTerm} OR phone2:${searchTerm} OR phone3:${searchTerm} OR billingPhone:${searchTerm})`;
    }

    // Adding isDeleted condition if requested
    if (includeIsDeleted) {
      queryString += ` AND (isDeleted:0)`;
    }

    // Exclude archived records
    queryString += ' AND NOT status:"Archive"';

    return queryString;
  }

  private generateSearchClause(field: string, searchTerm: string) {
    if (searchTerm) {
      // If searchTerm is provided and includes a space character
      if (searchTerm.length && searchTerm.includes(' ')) {
        // Split the searchTerm into two parts at the space character
        const pos = searchTerm.indexOf(' ');
        const first = searchTerm.substring(0, pos);
        const last = searchTerm.substring(pos + 1);

        // Return a complex search clause that searches for both parts of the searchTerm
        // in the specified field, either as separate words or as a single word
        return `(((${field}:(${first}) OR ${field}:(${first}*))
          AND (${field}:(${last}) OR ${field}:(${last}*)))
          OR ((${field}:(${first}${last}) OR ${field}:(${first}*${last}*))
          OR (${field}:(${first}${last}) OR ${field}:(${first}*${last}*))))`;
      } else if (searchTerm.length) {
        return `(${field}:(${searchTerm}) OR ${field}:(${searchTerm}*))`;
      }
      // If searchTerm is provided but is an empty string, return an empty string
      return '';
    }
    return '';
  }

  private static getUrlEndpoint(token: string): string {
    const jwtPayload = jwtDecode<any>(token);
    const endpointUrl = jwtPayload['restUrl'];
    return `${endpointUrl}/`;
  }

  getValidDomainsForCorporation(corporationId: string): Observable<string[]> {
    return this.query2(BullhornEntityTypes.CorporateUser, {
      query: `corporation.id=${corporationId} AND isDeleted=false AND isHidden=0 AND status <> 'Archive'`,
      fields: 'email',
    }).pipe(
      map((users: any[]) => {
        const domains = new Set<string>();
        users.forEach((user) => {
          if (user.email && typeof user.email === 'string') {
            const domain = user.email.split('@')[1]?.toLowerCase();
            if (domain) {
              domains.add(domain);
            }
          }
        });
        return Array.from(domains);
      }),
    );
  }

  private retrieveRecords(params: any): Observable<any> {
    const searchParamType = params.searchType === 'search' ? 'query' : 'where';
    const url = this.buildApiUrl(`${params.searchType || 'query'}/${params.entity.type}`);
    const firstRequestUrl =
      url + `?fields=${params.fields}&count=1&showTotalMatched=true&${searchParamType}=${encodeURIComponent(params.query)}`;

    const buildObs = (first: any) => {
      const totalToPull = first.total;
      const pageSize = 500;
      const pageCount = Math.ceil(totalToPull / pageSize);
      const obs: any[] = [];
      for (let i = 1; i <= pageCount; i++) {
        const start = (i - 1) * pageSize;
        const parameters = `?${searchParamType}=${encodeURIComponent(params.query)}&fields=${
          params.fields
        }&start=${start}&count=${pageSize}`;
        obs.push(this.http.get<any>(url + parameters));
      }
      return obs;
    };

    const first$ = this.http.get<any>(firstRequestUrl);
    return first$.pipe(
      map((first) => buildObs(first)),
      concatMap((obs) =>
        forkJoin(...obs).pipe(
          concatMap((r) => r.map((rr) => (rr as { data: any }).data)),
          mergeAll(),
          toArray(),
        ),
      ),
    );
  }
}
